
import React, { useState } from 'react';

const WhyLawyer: React.FC = () => {
  const [openSection, setOpenSection] = useState<number | null>(1);

  const toggleSection = (id: number) => {
    setOpenSection(openSection === id ? null : id);
  };

  const sections = [
    {
      id: 1,
      title: '遺産分割の現場で見えてきた「負動産」の深刻さ',
      icon: 'ri-scales-3-line',
      color: 'border-blue-100',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      id: 2,
      title: '不動産会社のビジネスモデルでは「負動産」は扱えない',
      icon: 'ri-building-line',
      color: 'border-red-100',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600'
    },
    {
      id: 3,
      title: '農業委員会、隣接地所有者、相手方との条件交渉――すべて弁護士の得意分野',
      icon: 'ri-discuss-line',
      color: 'border-purple-100',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      id: 4,
      title: '「タダであげる」取引こそ、契約書が必須',
      icon: 'ri-shield-check-line',
      color: 'border-orange-100',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600'
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-blue-50 to-indigo-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            なぜ不動産の処分を弁護士に依頼するのか？
          </h2>
          
          <p className="text-xl text-gray-600 mb-12 max-w-4xl mx-auto">
            「不動産のことは不動産会社に」と思われるかもしれません。<br />
            しかし、価値のない「負動産」の処分は、不動産会社の領域を超えた法的問題なのです。
          </p>
        </div>

        <div className="space-y-4">
          {/* ① 弁護士の実体験：負動産は相続紛争の火種 */}
          <div className="bg-white rounded-2xl shadow-lg border border-blue-100 overflow-hidden">
            <button
              onClick={() => toggleSection(1)}
              className="w-full p-6 text-left hover:bg-blue-50 transition-colors duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-scales-3-line text-blue-600 text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    遺産分割の現場で見えてきた「負動産」の深刻さ
                  </h3>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <i className={`ri-arrow-${openSection === 1 ? 'up' : 'down'}-s-line text-2xl text-gray-500 transition-transform duration-200`}></i>
                </div>
              </div>
            </button>

            <div className={`transition-all duration-500 ease-in-out ${
              openSection === 1 
                ? 'max-h-[2000px] opacity-100' 
                : 'max-h-0 opacity-0'
            } overflow-hidden`}>
              <div className="px-6 pb-6">
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p>
                    遺産を巡る法的紛争の際、価値のない不動産の処分が問題となることは日常茶飯事です。
                  </p>
                  
                  <div className="bg-red-50 p-6 rounded-xl border border-red-100">
                    <div className="space-y-2 text-red-700">
                      <p>「この山林、誰が相続するの？」</p>
                      <p>「誰も欲しくないなら、固定資産税は誰が払うの？」</p>
                      <p>「勝手に処分されたら困る」</p>
                    </div>
                  </div>
                  
                  <p>
                    ――こうした意見の対立が、遺産分割協議を長期化させ、相続人同士の関係を悪化させます。
                  </p>
                  
                  <p>
                    弁護士は様々な種類の負動産処分に体当たりで取り組んでいます。私もその一人ですが、すでに紛争が始まってしまってから相続人が足並みを揃えて処分することは極めて困難であり、それを理由にさらに争いが激化することも珍しくありません。
                  </p>
                  
                  <p>
                    だからこそ、紛争になる前に、負動産を早期に処分することが重要なのです。
                  </p>
                  
                  <p className="font-semibold">
                    弁護士は、法的紛争の現場で「負動産がいかに厄介か」を身をもって知っています。<br />
                    そして、どうすれば処分できるかの引き出しも持っているのです。
                  </p>
                </div>
                
                <div className="mt-6 bg-blue-50 p-6 rounded-xl border border-blue-200">
                  <h4 className="font-bold text-blue-800 mb-3 flex items-center">
                    <i className="ri-lightbulb-line mr-2"></i>
                    弁護士だからこそ知っている
                  </h4>
                  <p className="text-blue-700">
                    相続が発生してから「負動産をどうするか」で揉めるより、生前または相続直後に処分する方が、はるかにスムーズです。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ② 不動産業者が取り扱うのは「売れる」不動産だけ */}
          <div className="bg-white rounded-2xl shadow-lg border border-red-100 overflow-hidden">
            <button
              onClick={() => toggleSection(2)}
              className="w-full p-6 text-left hover:bg-red-50 transition-colors duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-building-line text-red-600 text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    不動産会社のビジネスモデルでは「負動産」は扱えない
                  </h3>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <i className={`ri-arrow-${openSection === 2 ? 'up' : 'down'}-s-line text-2xl text-gray-500 transition-transform duration-200`}></i>
                </div>
              </div>
            </button>

            <div className={`transition-all duration-500 ease-in-out ${
              openSection === 2 
                ? 'max-h-[2000px] opacity-100' 
                : 'max-h-0 opacity-0'
            } overflow-hidden`}>
              <div className="px-6 pb-6">
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p>
                    一般的な不動産会社は「仲介手数料」で成り立っています。<br />
                    売買が成立して初めて報酬が得られるため、「売れる不動産」しか積極的に扱いません。
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <i className="ri-close-circle-line text-red-500 mt-1"></i>
                      <span>山林、農地、市街化調整区域 → 「買い手がつかない」</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-close-circle-line text-red-500 mt-1"></i>
                      <span>老朽化した戸建て → 「取り壊し費用が高額で売れない」</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-close-circle-line text-red-500 mt-1"></i>
                      <span>境界不明確な土地 → 「まず測量してください」</span>
                    </div>
                  </div>
                  
                  <p>
                    なぜなら、彼らのビジネスモデルでは、<br />
                    「時間をかけても売れない不動産」に労力を割くインセンティブがないからです。
                  </p>
                  
                  <p className="font-semibold">
                    一方、弁護士は「問題解決」が仕事です。<br />
                    売買ではなく「無償譲渡」という選択肢を使い、法的交渉で処分を実現します。
                  </p>
                </div>
                
                <div className="mt-6 bg-green-50 p-6 rounded-xl border border-green-200">
                  <h4 className="font-bold text-green-800 mb-3 flex items-center">
                    <i className="ri-lightbulb-line mr-2"></i>
                    不動産会社と弁護士の違い
                  </h4>
                  <div className="space-y-2 text-green-700">
                    <p><strong>不動産会社：</strong>「売れる不動産」を「買いたい人」に仲介する</p>
                    <p><strong>弁護士：</strong>「処分したい不動産」を「法的交渉」で処分する</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ③ 交渉は弁護士の十八番 */}
          <div className="bg-white rounded-2xl shadow-lg border border-purple-100 overflow-hidden">
            <button
              onClick={() => toggleSection(3)}
              className="w-full p-6 text-left hover:bg-purple-50 transition-colors duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-discuss-line text-purple-600 text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    農業委員会、隣接地所有者、相手方との条件交渉――すべて弁護士の得意分野
                  </h3>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <i className={`ri-arrow-${openSection === 3 ? 'up' : 'down'}-s-line text-2xl text-gray-500 transition-transform duration-200`}></i>
                </div>
              </div>
            </button>

            <div className={`transition-all duration-500 ease-in-out ${
              openSection === 3 
                ? 'max-h-[2000px] opacity-100' 
                : 'max-h-0 opacity-0'
            } overflow-hidden`}>
              <div className="px-6 pb-6">
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p>
                    負動産の処分には、さまざまな「交渉」が必要です。
                  </p>
                  
                  <div className="grid md:grid-cols-1 gap-6">
                    <div className="bg-amber-50 p-6 rounded-xl border border-amber-200">
                      <h4 className="font-bold text-amber-800 mb-3">【農業委員会との交渉】</h4>
                      <p className="text-amber-700 mb-3">
                        農地の譲渡には農業委員会の許可が必須。<br />
                        「なぜこの人に譲渡したいのか」<br />
                        「農業に使われる見込みはあるのか」<br />
                        ――こうした質問に、法的根拠を示しながら説得する必要があります。
                      </p>
                    </div>
                    
                    <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                      <h4 className="font-bold text-blue-800 mb-3">【隣接地所有者との交渉】</h4>
                      <p className="text-blue-700 mb-3">
                        「この土地、タダでもらってくれませんか？」<br />
                        こんな提案を、いきなり個人がするのはハードルが高いものです。<br />
                        弁護士名義で正式に提案することで、相手も真剣に検討してくれます。
                      </p>
                    </div>
                    
                    <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                      <h4 className="font-bold text-green-800 mb-3">【譲受希望者との条件交渉】</h4>
                      <p className="text-green-700 mb-3">
                        「取り壊し費用はどちらが負担するのか」<br />
                        「登記費用は？」<br />
                        「境界が不明確だけど、どうする？」<br />
                        こうした条件の詰めは、法的知識がなければ後々トラブルになります。
                      </p>
                    </div>
                  </div>
                  
                  <p className="font-semibold">
                    弁護士は日常的に「利害が対立する相手」と交渉し、合意を形成する専門家です。<br />
                    負動産処分の交渉も、弁護士にとっては日常業務の延長線上にあります。
                  </p>
                </div>
                
                <div className="mt-6 bg-purple-50 p-6 rounded-xl border border-purple-200">
                  <h4 className="font-bold text-purple-800 mb-3 flex items-center">
                    <i className="ri-lightbulb-line mr-2"></i>
                    交渉のプロだから実現できる
                  </h4>
                  <p className="text-purple-700">
                    個人では断られる提案も、弁護士名義なら「ちゃんとした話」として受け取られます。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ④ 負動産取引は法的リスクが極めて高い */}
          <div className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden">
            <button
              onClick={() => toggleSection(4)}
              className="w-full p-6 text-left hover:bg-orange-50 transition-colors duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-shield-check-line text-orange-600 text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    「タダであげる」取引こそ、契約書が必須
                  </h3>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <i className={`ri-arrow-${openSection === 4 ? 'up' : 'down'}-s-line text-2xl text-gray-500 transition-transform duration-200`}></i>
                </div>
              </div>
            </button>

            <div className={`transition-all duration-500 ease-in-out ${
              openSection === 4 
                ? 'max-h-[2000px] opacity-100' 
                : 'max-h-0 opacity-0'
            } overflow-hidden`}>
              <div className="px-6 pb-6">
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p>
                    「無償であげるんだから、契約書なんて必要ないでしょう？」<br />
                    ――これは大きな誤解です。
                  </p>
                  
                  <p className="font-semibold">
                    むしろ、無償譲渡だからこそ、後々のトラブルリスクが高いのです。
                  </p>
                  
                  <div className="bg-red-50 p-6 rounded-xl border border-red-200">
                    <h4 className="font-bold text-red-800 mb-3">【よくあるトラブル】</h4>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-3">
                        <i className="ri-close-circle-line text-red-500 mt-1"></i>
                        <span className="text-red-700">「雨漏りがあるなんて聞いてない。修理費を払え」</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <i className="ri-close-circle-line text-red-500 mt-1"></i>
                        <span className="text-red-700">「境界が実際と違う。責任を取れ」</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <i className="ri-close-circle-line text-red-500 mt-1"></i>
                        <span className="text-red-700">「地中に産廃が埋まっていた。撤去費用を請求する」</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <i className="ri-close-circle-line text-red-500 mt-1"></i>
                        <span className="text-red-700">「農業委員会の許可が下りなかった。話が違う」</span>
                      </div>
                    </div>
                  </div>
                  
                  <p>
                    こうしたトラブルを防ぐには、事案に即した詳細な契約書が不可欠です。
                  </p>
                  
                  <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                    <h4 className="font-bold text-green-800 mb-3">【弁護士が作成する譲渡契約書のポイント】</h4>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-3">
                        <i className="ri-check-line text-green-500 mt-1"></i>
                        <span className="text-green-700">瑕疵担保責任の明確化（「知っている範囲のみ告知、それ以外は責任を負わない」等）</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <i className="ri-check-line text-green-500 mt-1"></i>
                        <span className="text-green-700">現況有姿での引き渡し条件</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <i className="ri-check-line text-green-500 mt-1"></i>
                        <span className="text-green-700">境界不明確の場合の取り決め</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <i className="ri-check-line text-green-500 mt-1"></i>
                        <span className="text-green-700">取り壊し費用・登記費用の負担区分</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <i className="ri-check-line text-green-500 mt-1"></i>
                        <span className="text-green-700">農業委員会許可等の停止条件</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <i className="ri-check-line text-green-500 mt-1"></i>
                        <span className="text-green-700">紛争解決条項（万が一の際の対応）</span>
                      </div>
                    </div>
                  </div>
                  
                  <p>
                    不動産会社が用意する定型的な契約書では、こうした個別事情に対応できません。
                  </p>
                  
                  <p className="font-semibold">
                    弁護士は、あなたの物件の特性とリスクを踏まえた「オーダーメイドの契約書」を作成し、<br />
                    譲渡後のトラブルを未然に防ぎます。
                  </p>
                </div>
                
                <div className="mt-6 bg-orange-50 p-6 rounded-xl border border-orange-200">
                  <h4 className="font-bold text-orange-800 mb-3 flex items-center">
                    <i className="ri-lightbulb-line mr-2"></i>
                    契約書がないと...
                  </h4>
                  <p className="text-orange-700">
                    譲渡後に相手から「聞いてない」「責任を取れ」と言われても、反論できません。<br />
                    弁護士が作成する契約書は、あなたを守る「法的な盾」です。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* セクション末尾の総括 */}
        <div className="mt-16 bg-orange-50 border-2 border-orange-200 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            まとめ：なぜ弁護士なのか？
          </h3>
          
          <div className="space-y-4 text-gray-700">
            <div className="flex items-start space-x-3">
              <i className="ri-check-line text-orange-500 mt-1"></i>
              <span>弁護士は相続紛争の現場で、負動産処分の困難さを知り尽くしている</span>
            </div>
            <div className="flex items-start space-x-3">
              <i className="ri-check-line text-orange-500 mt-1"></i>
              <span>不動産会社は「売れる不動産」しか扱わないが、弁護士は「問題解決」が仕事</span>
            </div>
            <div className="flex items-start space-x-3">
              <i className="ri-check-line text-orange-500 mt-1"></i>
              <span>農業委員会や隣接地所有者との交渉は、弁護士の得意分野</span>
            </div>
            <div className="flex items-start space-x-3">
              <i className="ri-check-line text-orange-500 mt-1"></i>
              <span>負動産取引は法的リスクが高く、事案に即した契約書作成が必須</span>
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-lg font-semibold text-gray-900">
              負動産の処分は、単なる不動産取引ではなく「法的紛争の予防」です。<br />
              だからこそ、弁護士に依頼する価値があるのです。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyLawyer;
